import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Minimal Ad Carousel - Apple-style design
 * Ubicación: Header de páginas principales
 */
export function AdCarousel({ className = '', variant = 'default' }) {
  const [ads, setAds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAds();
  }, []);

  useEffect(() => {
    if (ads.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % ads.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [ads.length]);

  const fetchAds = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ads/active`);
      if (response.ok) {
        const data = await response.json();
        setAds(data.header_carousel || []);
      }
    } catch (err) {
      console.error('Error fetching ads:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || ads.length === 0) return null;

  const heightClass = variant === 'compact' ? 'h-24 md:h-28' : 'h-32 md:h-40';

  return (
    <div 
      className={`relative w-full overflow-hidden rounded-2xl bg-gray-900/50 backdrop-blur-sm ${className}`} 
      data-testid="ad-carousel"
    >
      <div className={`relative ${heightClass}`}>
        <AnimatePresence mode="wait">
          <motion.a
            key={currentIndex}
            href={ads[currentIndex]?.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            className="absolute inset-0 block cursor-pointer group"
            onClick={(e) => {
              if (!ads[currentIndex]?.linkUrl) e.preventDefault();
            }}
          >
            <img
              src={ads[currentIndex]?.imageUrl}
              alt={ads[currentIndex]?.clientName || 'Anuncio'}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            
            {/* Hover indicator */}
            {ads[currentIndex]?.linkUrl && (
              <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="flex items-center gap-1 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-900">
                  Visitar <ExternalLink className="w-3 h-3" />
                </div>
              </div>
            )}
          </motion.a>
        </AnimatePresence>

        {/* Navigation - Only show if multiple ads */}
        {ads.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.preventDefault();
                setCurrentIndex((currentIndex - 1 + ads.length) % ads.length);
              }}
              className="absolute left-3 top-1/2 -translate-y-1/2 p-1.5 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full text-white transition-all duration-200"
              aria-label="Anterior"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                setCurrentIndex((currentIndex + 1) % ads.length);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full text-white transition-all duration-200"
              aria-label="Siguiente"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Minimal dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {ads.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentIndex(i);
                  }}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                    i === currentIndex ? 'bg-white w-4' : 'bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={`Ir a anuncio ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}

        {/* Subtle sponsored badge */}
        <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/30 backdrop-blur-sm rounded-full text-[10px] text-white/60 font-medium tracking-wide uppercase">
          Ad
        </div>
      </div>
    </div>
  );
}

/**
 * Sidebar Ad - Vertical banner, Apple-style
 * Ubicación: Lateral en listados
 */
export function SidebarAd({ className = '' }) {
  const [ad, setAd] = useState(null);

  useEffect(() => {
    fetchAd();
  }, []);

  const fetchAd = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ads/active`);
      if (response.ok) {
        const data = await response.json();
        if (data.sidebar_banner?.length > 0) {
          setAd(data.sidebar_banner[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching sidebar ad:', err);
    }
  };

  if (!ad) return null;

  return (
    <motion.a
      href={ad.linkUrl}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`block relative overflow-hidden rounded-2xl bg-gray-900/50 backdrop-blur-sm group cursor-pointer ${className}`}
      data-testid="sidebar-ad"
      onClick={(e) => {
        if (!ad.linkUrl) e.preventDefault();
      }}
    >
      <img
        src={ad.imageUrl}
        alt={ad.clientName || 'Anuncio'}
        className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-[1.02]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      {/* Hover CTA */}
      {ad.linkUrl && (
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="flex items-center gap-1 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-gray-900">
            Ver más <ExternalLink className="w-3.5 h-3.5" />
          </div>
        </div>
      )}
      
      <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/30 backdrop-blur-sm rounded-full text-[10px] text-white/60 font-medium tracking-wide uppercase">
        Ad
      </div>
    </motion.a>
  );
}

/**
 * Featured Ad Cards - Grid de destacados, Apple-style
 * Ubicación: Entre resultados de búsqueda
 */
export function FeaturedAd({ className = '', limit = 2 }) {
  const [ads, setAds] = useState([]);

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ads/active`);
      if (response.ok) {
        const data = await response.json();
        setAds((data.featured_listing || []).slice(0, limit));
      }
    } catch (err) {
      console.error('Error fetching featured ads:', err);
    }
  };

  if (ads.length === 0) return null;

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${className}`} data-testid="featured-ads">
      {ads.map((ad, index) => (
        <motion.a
          key={ad.id || index}
          href={ad.linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: index * 0.1 }}
          className="relative block overflow-hidden rounded-2xl bg-gray-900/50 backdrop-blur-sm group cursor-pointer"
          onClick={(e) => {
            if (!ad.linkUrl) e.preventDefault();
          }}
        >
          <img
            src={ad.imageUrl}
            alt={ad.clientName || 'Destacado'}
            className="w-full h-28 md:h-36 object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          
          {/* Content */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <p className="text-white text-sm font-medium truncate">{ad.clientName}</p>
            {ad.description && (
              <p className="text-white/70 text-xs truncate mt-0.5">{ad.description}</p>
            )}
          </div>
          
          {/* Badge */}
          <div className="absolute top-2 right-2 px-2 py-0.5 bg-emerald-500/90 backdrop-blur-sm rounded-full text-[10px] text-white font-medium tracking-wide">
            Destacado
          </div>
          
          {/* Hover effect */}
          {ad.linkUrl && (
            <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="flex items-center gap-1 px-2 py-1 bg-white/90 backdrop-blur-sm rounded-full text-[10px] font-medium text-gray-900">
                Visitar <ExternalLink className="w-2.5 h-2.5" />
              </div>
            </div>
          )}
        </motion.a>
      ))}
    </div>
  );
}

/**
 * Inline Ad Banner - Horizontal minimalista para dashboards
 * Ubicación: Entre secciones de dashboard
 */
export function InlineAdBanner({ className = '' }) {
  const [ad, setAd] = useState(null);

  useEffect(() => {
    fetchAd();
  }, []);

  const fetchAd = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ads/active`);
      if (response.ok) {
        const data = await response.json();
        // Usa featured o header, el primero disponible
        const available = data.featured_listing?.[0] || data.header_carousel?.[0];
        if (available) setAd(available);
      }
    } catch (err) {
      console.error('Error fetching inline ad:', err);
    }
  };

  if (!ad) return null;

  return (
    <motion.a
      href={ad.linkUrl}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className={`block relative overflow-hidden rounded-xl bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-sm border border-white/5 group cursor-pointer ${className}`}
      data-testid="inline-ad-banner"
      onClick={(e) => {
        if (!ad.linkUrl) e.preventDefault();
      }}
    >
      <div className="flex items-center gap-4 p-3">
        {/* Thumbnail */}
        <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden">
          <img
            src={ad.imageUrl}
            alt={ad.clientName || 'Anuncio'}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] text-emerald-400/80 font-medium uppercase tracking-wider">Patrocinado</span>
          </div>
          <p className="text-white font-medium text-sm truncate">{ad.clientName}</p>
          {ad.description && (
            <p className="text-gray-400 text-xs truncate mt-0.5">{ad.description}</p>
          )}
        </div>
        
        {/* CTA */}
        {ad.linkUrl && (
          <div className="flex-shrink-0 hidden sm:flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full text-xs text-white font-medium transition-colors">
            Ver <ExternalLink className="w-3 h-3" />
          </div>
        )}
      </div>
    </motion.a>
  );
}
